/**
 * Shared Zod v4 schemas used across contracts.
 * Defines reusable validation schemas for API request/response types.
 */
import { z } from 'zod';
// --- Pagination ---
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().min(1).default(1).optional(),
    pageSize: z.coerce.number().min(1).max(100).default(20).optional(),
});
export const PaginationMetaSchema = z.object({
    page: z.number(),
    pageSize: z.number(),
    totalItems: z.number(),
    totalPages: z.number(),
    hasNextPage: z.boolean(),
});
// --- Date Range ---
export const DateRangeQuerySchema = z.object({
    startDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Date must be YYYY-MM-DD' })
        .optional(),
    endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Date must be YYYY-MM-DD' })
        .optional(),
});
export const DatePathParamSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Date must be YYYY-MM-DD' }),
});
// --- Error Response ---
export const ApiErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
});
export const ErrorResponseSchema = z.object({
    success: z.literal(false),
    error: ApiErrorSchema,
});
// --- Auth ---
export const SessionUserSchema = z.object({
    ssoUserId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    githubUsername: z.string(),
    avatar: z.string().nullable(),
});
// --- Employee ---
export const EmployeeSchema = z.object({
    employeeId: z.string(),
    name: z.string(),
    email: z.email(),
    githubUsername: z.string(),
    jiraAccountId: z.string(),
    slackUserId: z.string(),
    jobkanEmployeeId: z.string(),
    mode: z.enum(['engineering', 'standard']),
    teamId: z.string(),
    managerId: z.string(),
    isActive: z.boolean(),
    deactivatedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const EmployeeCreateSchema = z.object({
    name: z.string().min(1),
    email: z.email(),
    githubUsername: z.string(),
    jiraAccountId: z.string(),
    slackUserId: z.string(),
    jobkanEmployeeId: z.string().optional(),
    mode: z.enum(['engineering', 'standard']),
    teamId: z.string(),
    managerId: z.string(),
});
export const EmployeeUpdateSchema = z.object({
    name: z.string().min(1).optional(),
    email: z.email().optional(),
    githubUsername: z.string().optional(),
    jiraAccountId: z.string().optional(),
    slackUserId: z.string().optional(),
    jobkanEmployeeId: z.string().optional(),
    mode: z.enum(['engineering', 'standard']).optional(),
    teamId: z.string().optional(),
    managerId: z.string().optional(),
    isActive: z.boolean().optional(),
});
export const ServiceTypeSchema = z.enum(['slack', 'github', 'jira', 'jobkan']);
export const LinkAccountSchema = z.object({
    service: ServiceTypeSchema,
    identifier: z.string().min(1),
});
export const UnlinkPathParamsSchema = z.object({
    employeeId: z.string(),
    service: ServiceTypeSchema,
});
export const BulkModeSchema = z.object({
    employeeIds: z.array(z.string().min(1)).min(1),
    mode: z.enum(['engineering', 'standard']),
});
export const BulkModeResultSchema = z.object({
    updated: z.number(),
    failed: z.number(),
    errors: z.array(z.object({
        employeeId: z.string(),
        message: z.string(),
    })),
});
export const BatchResolveSchema = z.object({
    slackIds: z.array(z.string()).optional(),
    githubUsernames: z.array(z.string()).optional(),
});
export const BatchResolveResultSchema = z.record(z.string(), EmployeeSchema.nullable());
// --- Report ---
export const ParsedTaskSchema = z.object({
    description: z.string(),
    jiraTicketId: z.string().optional(),
    hoursSpent: z.number().optional(),
});
export const StoredReportSchema = z.object({
    employeeSlackId: z.string(),
    date: z.string(),
    rawText: z.string(),
    yesterday: z.array(ParsedTaskSchema),
    today: z.array(ParsedTaskSchema),
    blockers: z.array(z.string()),
    notes: z.string(),
    jiraTickets: z.array(z.string()),
    submittedAt: z.string(),
    updatedAt: z.string(),
    slackMessageTs: z.string(),
    threadTs: z.string(),
    parseConfidence: z.enum(['template', 'free_text']),
});
export const ReportComplianceSchema = z.object({
    employeeId: z.string(),
    month: z.string(),
    workingDays: z.number(),
    reportedDays: z.number(),
    complianceRate: z.number(),
    missingDates: z.array(z.string()),
});
// --- Verification ---
export const EvidenceSourceSchema = z.object({
    type: z.enum([
        'github_commit',
        'github_pr',
        'github_review',
        'jira_activity',
        'jira_comment',
        'jira_time_log',
        'slack_message',
        'jobkan_hours',
    ]),
    url: z.string().optional(),
    summary: z.string(),
    timestamp: z.string(),
});
export const TaskVerificationScoreSchema = z.object({
    jiraTicketId: z.string(),
    description: z.string(),
    score: z.number().min(0).max(100),
    confidence: z.number().min(0).max(100),
    findings: z.array(z.string()),
    evidenceSources: z.array(EvidenceSourceSchema),
});
export const VerificationResultSchema = z.object({
    verificationId: z.string(),
    reportId: z.string(),
    employeeId: z.string(),
    date: z.string(),
    state: z.enum([
        'pending',
        'verified_green',
        'verified_yellow',
        'flagged_red',
        'reverified',
        'manually_reviewed',
        'missing',
    ]),
    overallScore: z.number().min(0).max(100),
    taskScores: z.array(TaskVerificationScoreSchema),
    llmTier: z.enum(['haiku', 'sonnet', 'opus']),
    verifiedAt: z.string(),
    reviewedBy: z.string().optional(),
});
// --- Scores ---
export const WeeklyScoreSchema = z.object({
    employeeId: z.string(),
    weekStart: z.string(),
    averageScore: z.number(),
    trend: z.enum(['up', 'down', 'stable']),
    reportCount: z.number(),
    flagCount: z.number(),
});
export const MonthlyScoreSchema = z.object({
    employeeId: z.string(),
    month: z.string(),
    averageScore: z.number(),
    hoursWorked: z.number(),
    complianceRate: z.number(),
    topContributions: z.array(z.string()),
});
// --- Notification Config ---
export const NotificationConfigSchema = z.object({
    redFlagAlertsEnabled: z.boolean(),
    weeklySummaryEnabled: z.boolean(),
    complianceAlertsEnabled: z.boolean(),
    notificationChannelId: z
        .string()
        .regex(/^(C[A-Z0-9]+)?$/)
        .or(z.literal('')),
});
// --- Settings ---
export const SystemSettingsSchema = z.object({
    standupChannelId: z.string(),
    standupChannelName: z.string(),
    standupTime: z.string(),
    standupTimezone: z.string(),
    standupTemplate: z.string(),
    managerChannelName: z.string(),
    verificationEnabled: z.boolean(),
    notificationConfig: NotificationConfigSchema.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SettingsUpdateSchema = z.object({
    standupChannelId: z.string().optional(),
    standupChannelName: z.string().optional(),
    standupTime: z.string().optional(),
    standupTemplate: z.string().optional(),
    managerChannelName: z.string().optional(),
    verificationEnabled: z.boolean().optional(),
    notificationConfig: NotificationConfigSchema.partial().optional(),
});
// --- Feature Flags ---
export const FeatureFlagSchema = z.object({
    flagId: z.string(),
    name: z.string(),
    enabled: z.boolean(),
    description: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string(),
});
// --- Collection Pipeline ---
export const CollectionSourceSchema = z.enum(['github', 'jira', 'slack', 'jobkan']);
export const ExecutionStatusSchema = z.enum(['running', 'completed', 'partial', 'failed']);
export const CollectorResultStatusSchema = z.enum(['success', 'partial', 'error']);
export const SourceResultSchema = z.object({
    status: CollectorResultStatusSchema,
    itemCount: z.number(),
    errorMessage: z.string().optional(),
});
export const CollectorResultSchema = z.object({
    source: CollectionSourceSchema,
    status: CollectorResultStatusSchema,
    itemCount: z.number(),
    errorMessage: z.string().optional(),
    s3Key: z.string().optional(),
});
export const ExecutionRecordSchema = z.object({
    executionId: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    scope: z.string(),
    status: ExecutionStatusSchema,
    triggeredBy: z.enum(['schedule', 'manual']),
    requestedBy: z.string(),
    sourceResults: z.record(CollectionSourceSchema, SourceResultSchema).optional(),
    failedSources: z.array(CollectionSourceSchema),
    startedAt: z.string(),
    completedAt: z.string().optional(),
});
export const CollectionTriggerSchema = z.object({
    employeeId: z.string().optional(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Date must be YYYY-MM-DD' }),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Date must be YYYY-MM-DD' }),
    force: z.boolean().default(false),
});
export const MonitoredChannelSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
});
export const CollectionConfigSchema = z.object({
    scheduleExpression: z.string(),
    monitoredSlackChannels: z.array(MonitoredChannelSchema),
    jobkanEnabled: z.boolean(),
    jiraProjects: z.array(z.string()),
    githubRepos: z.array(z.string()),
    evidenceTtlDays: z.number(),
    updatedAt: z.string(),
});
//# sourceMappingURL=schemas.js.map